import splitArrayKey from '@bybeam/platform-lib/answers/splitArrayKey';
import {
  ApplicantProfile,
  ApplicantProfileConfiguration,
  Application,
  ApplicationAnswers,
  ApplicationContentInput,
  ApplicationQuestion,
  ApplicationQuestionGroup,
  ApplicationQuestionKeys,
  Field,
  FieldType,
} from '@bybeam/platform-types';
import {
  GenericFormData,
  ValidationConfig,
  optionalField,
} from '@platform-ui-common/hooks/useForm/index.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { flatten } from 'flat';
import { makeRoute } from '../Routes.js';
import { getApplicantProfileConfig } from '../applicantProfile.js';
import { evaluate } from './ast/evaluate.js';
import {
  getAddressAnswers,
  getFieldAnswersValue,
  getFieldFormInit,
  getFieldUpdate,
  makeSkipIndicator,
} from './fields/index.js';
import mergeUpdates from './mergeUpdates.js';
import { safeKey } from './utils.js';

export type ApplicationMode = 'new' | 'edit';

// Returns the parameters for the `useForm` hook initialization for the given question.
// Merge the initial state and validation for each field in the question to produce
// a single initial state/validation that encompasses all the fields.
export function getQuestionFormInit<FormData extends GenericFormData>(
  question: ApplicationQuestion,
  answers: Partial<ApplicationAnswers>,
  application: Application,
): [FormData, ValidationConfig<FormData>] {
  const skippedKey = [question.key, 'skipped'].join('.');
  const skipped = !!getFieldAnswersValue(skippedKey, answers);
  return question.fields.reduce(
    ([data, config], field) => {
      const [fieldData, fieldConfig] = getFieldFormInit(
        field,
        skipped ? {} : answers,
        application,
      );

      return [
        {
          ...data,
          ...fieldData,
          ...(question.skippable && { [skippedKey]: skipped }),
        },
        {
          ...config,
          // When multiple fields share the same key (e.g. a document upload shown
          // for each dropdown option), OR their conditions so the field is required
          // whenever any one of the active dynamicLogic conditions is met.
          ...Object.fromEntries(
            Object.entries(fieldConfig).map(([key, entry]) => {
              const prev = config[key];
              const a = prev?.condition;
              const b = entry.condition;
              return [
                key,
                prev
                  ? {
                      ...prev,
                      ...entry,
                      condition:
                        a && b ? (d: FormData) => a(d) || b(d) : undefined,
                    }
                  : entry,
              ];
            }),
          ),
          ...(question.skippable && { [skippedKey]: optionalField() }),
        } as ValidationConfig<FormData>,
      ];
    },
    [{} as FormData, {}],
  );
}

// Given the useForm data for the question, returns the partial of the answers to
// be updated. Returns the full structure of the answers at the top level key of the
// question (e.g., if the question key is financialHardship.experience, it would
// merge the complete existing tree at answers.financialHardship). This means it is
// important that the keys for all of the question's fields have the same top-level
// key as the question, to avoid accidentally overwriting any existing answers.
export function getQuestionUpdate<FormData extends GenericFormData>(
  question: ApplicationQuestion,
  answers: Partial<ApplicationAnswers>,
  update: FormData,
): ApplicationContentInput {
  const highestKey = question.key.split('.')[0];
  const fields: Field[] = question.skippable
    ? [...question.fields, makeSkipIndicator(question.key)]
    : question.fields;
  return mergeUpdates([
    { answers: { [highestKey]: answers[highestKey] } },
    ...fields.map((field) => getFieldUpdate(field, update)),
  ]);
}

export function getFirstEnabledQuestionGroupPage(
  group: ApplicationQuestionGroup,
  answers: Partial<ApplicationAnswers>,
): string {
  return getQuestionRoute(
    group.questions.find((q) => isQuestionEnabled(q, answers)),
  );
}

export function getQuestionGroupRoute(
  group: ApplicationQuestionGroup,
  answers: Partial<ApplicationAnswers>,
): string {
  if (group.overview)
    return makeRoute(ApplicantRoutes.QUESTION_GROUP_OVERVIEW, {
      groupKey: group.key,
    });
  return getFirstEnabledQuestionGroupPage(group, answers);
}

export function getQuestionRoute(question: ApplicationQuestion): string {
  // For some reason using the key as-is with '.' in it works fine when routing there via
  // React Router (i.e., from a different page in the app), but if you refresh it totally
  // crashes and displays the browser default 404 page
  return makeRoute(ApplicantRoutes.QUESTION, {
    questionKey: safeKey(question.key),
  });
}

export function getQuestionFields(fields: Field[]): Field[] {
  return fields.filter((field) => FieldType.Typography !== field.type);
}

export function isPrefilled(
  fields: Field[],
  applicantProfile: ApplicantProfile,
  applicantProfileConfigs: ApplicantProfileConfiguration[],
  answers: Partial<ApplicationAnswers>,
) {
  const addressKeyRegex = /address/gi;
  const applicantProfileConfig = getApplicantProfileConfig(
    applicantProfileConfigs,
    applicantProfile,
  );

  return getQuestionFields(fields).some((field) => {
    let partnerConfigMatch = !!applicantProfileConfig?.profileKeys?.find(
      ({ key }) => key === field.key,
    );

    let applicantProfileMatch = undefined;
    let presentInAnswers = undefined;

    const profileKeys = Object.keys(
      // biome-ignore lint/suspicious/noExplicitAny: only considering the keys
      flatten<ApplicationAnswers, Record<string, any>[]>(
        applicantProfile?.answers ?? {},
      ),
    ).map((key) => splitArrayKey(key)?.key);

    if (field.key.match(addressKeyRegex) && field.type === FieldType.Address) {
      let addresses = undefined;
      if (`${field.key}Id` === ApplicationQuestionKeys.MailingAddress) {
        partnerConfigMatch = true;
        applicantProfileMatch = !!applicantProfile.mailingAddress?.id;
        addresses = applicantProfile.mailingAddress
          ? [applicantProfile.mailingAddress]
          : [];
      } else {
        applicantProfileMatch = !!profileKeys.find(
          (key) => key === `${field.key}Id`,
        );
        addresses = applicantProfile.addresses;
      }
      presentInAnswers = !!getAddressAnswers(field, answers, addresses)?.[
        `${field.key}.city`
      ];
    } else if (field.type === FieldType.Complex) {
      applicantProfileMatch = !!profileKeys.find((key) =>
        key.includes(field.key),
      );
      presentInAnswers = !!getFieldAnswersValue(field.key, answers);
    } else {
      applicantProfileMatch = !!profileKeys.find((key) => key === field.key);
      presentInAnswers = !!getFieldAnswersValue(field.key, answers);
    }

    return partnerConfigMatch && applicantProfileMatch && presentInAnswers;
  });
}

export function isQuestionGroupEnabled(
  group: ApplicationQuestionGroup,
  answers: Partial<ApplicationAnswers>,
): boolean {
  return !group.dynamicLogic || evaluate(group.dynamicLogic, answers);
}

export function isQuestionEnabled(
  question: ApplicationQuestion,
  answers: Partial<ApplicationAnswers>,
): boolean {
  return !question.dynamicLogic || evaluate(question.dynamicLogic, answers);
}
